/**
 * BuilderProgressOverlay — the universal "AI builds first" spinner.
 *
 * Every PayGBot builder mounts this while a first draft is being
 * generated. Fixed order matches the Universal Builder Standard:
 *
 *   🧠 Reading Business Brain
 *   🎨 Applying Brand Brain
 *   📊 Researching Market Intelligence
 *   ✍  Writing Content
 *   🎨 Applying Design
 *   📱 Optimizing Mobile
 *   ✅ Finalizing
 *
 * Steps advance on a fixed cadence — the actual generation runs
 * server-side and finishes when it finishes; the overlay's only job
 * is to make the "AI is doing work for you" feeling tangible.
 */
import React, { useEffect, useState } from 'react';
import { Brain, Palette, ChartBar, PencilLine, MagicWand, DeviceMobile, CheckCircle, Circle, Spinner } from '@phosphor-icons/react';

const STEPS = [
  { icon: Brain,        label: 'Reading Business Brain' },
  { icon: Palette,      label: 'Applying Brand Brain' },
  { icon: ChartBar,     label: 'Researching Market Intelligence' },
  { icon: PencilLine,   label: 'Writing content' },
  { icon: MagicWand,    label: 'Applying design' },
  { icon: DeviceMobile, label: 'Optimizing mobile' },
  { icon: CheckCircle,  label: 'Finalizing' },
];

const BuilderProgressOverlay = ({ visible, active = true, title = 'Building your first draft…' }) => {
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!visible || !active) {
      setStep(0);
      return undefined;
    }
    const id = setInterval(() => {
      setStep((s) => Math.min(s + 1, STEPS.length - 1));
    }, 2200);
    return () => clearInterval(id);
  }, [visible, active]);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-950/90 backdrop-blur-md flex items-center justify-center px-4"
      data-testid="builder-progress-overlay"
    >
      <div className="w-full max-w-lg rounded-2xl border border-slate-800 bg-slate-900 p-8">
        <div className="flex items-center gap-3 mb-6">
          <Spinner size={28} className="text-emerald-400 animate-spin" />
          <div>
            <div className="text-lg font-semibold text-white">{title}</div>
            <div className="text-xs text-slate-500">This usually takes 15–30 seconds.</div>
          </div>
        </div>

        <div className="space-y-2">
          {STEPS.map((s, idx) => {
            const done = idx < step;
            const current = idx === step;
            const Icon = done ? CheckCircle : (current ? s.icon : Circle);
            return (
              <div
                key={s.label}
                data-testid={`builder-step-${idx}`}
                className={`flex items-center gap-3 py-2 px-3 rounded-lg transition ${
                  current ? 'bg-emerald-500/10 border border-emerald-500/30' : ''
                }`}
              >
                <Icon
                  size={20}
                  weight={done ? 'fill' : (current ? 'duotone' : 'regular')}
                  className={
                    done ? 'text-emerald-400'
                    : current ? 'text-emerald-300 animate-pulse'
                    : 'text-slate-600'
                  }
                />
                <span
                  className={`text-sm ${
                    done ? 'text-slate-300 line-through decoration-emerald-500/60' :
                    current ? 'text-slate-100 font-medium' :
                    'text-slate-500'
                  }`}
                >
                  {s.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default BuilderProgressOverlay;
